import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell as BellGlyph } from 'lucide-react';
import { Icon } from '../design/Icon';
import { useSharedTodos } from './TodosContext';
import { markSeen, todoSeenKey, useSeenSet } from './seen';

/**
 * Notification bell — a count badge + dropdown of cross-tool to-dos (live).
 * Clicking an item deep-links to where the action is performed. Items vanish
 * as their underlying docs are actioned (by anyone), since they're live queries.
 *
 * The badge counts only UNSEEN to-dos: opening the bell marks every item in it
 * seen, so the number doesn't keep pulling the user back (Sep 2026). A new
 * to-do brings it back. Unseen items keep the plum dot in the list.
 */
export function Bell() {
  const todos = useSharedTodos();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const count = todos.length;
  const seen = useSeenSet();
  const unseen = todos.filter((t) => !seen.has(todoSeenKey(t))).length;
  // Mark on CLOSE, not on open, so the dots still show which items are new
  // while the dropdown is up.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) { wasOpen.current = true; return; }
    if (wasOpen.current) {
      wasOpen.current = false;
      markSeen(todos.map((t) => todoSeenKey(t)));
    }
  }, [open, todos]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center text-fg-muted hover:text-fg transition-colors duration-fast"
        aria-label={unseen > 0 ? `To-dos (${unseen} new)` : 'To-dos'}
        title="To-dos"
      >
        <Icon as={BellGlyph} size={20} />
        {unseen > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-solid text-white text-2xs !tracking-normal leading-4 text-center font-semibold">
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>

      {open && (
        // Phones: full width under the top bar instead of hanging off the left edge.
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-line bg-surface shadow-lg z-50 max-md:fixed max-md:inset-x-4 max-md:top-[calc(var(--app-header-h)+0.5rem)] max-md:mt-0 max-md:w-auto">
          <div className="px-4 py-2.5 border-b border-line text-sm font-medium text-fg">
            To-dos {count > 0 && <span className="text-fg-faint font-normal">· {count}</span>}
          </div>
          {count === 0 ? (
            <p className="px-4 py-6 text-sm text-fg-muted text-center">You&rsquo;re all caught up.</p>
          ) : (
            <ul className="divide-y divide-line">
              {todos.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate(t.link);
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-surface-2 flex items-start gap-2"
                  >
                    <span
                      className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${seen.has(todoSeenKey(t)) ? 'bg-transparent' : 'bg-brand-solid'}`}
                      aria-label={seen.has(todoSeenKey(t)) ? undefined : 'New'}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-fg truncate">{t.label}</span>
                      <span className="block text-xs text-fg-faint">{t.tool}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
